/**
 * SpecsJsonEditor — STORY-1306 Phase 2 slice A (proto #13).
 *
 * A mockup (02-type-editor.html JSON SPECS szekció) kv+raw kettős nézete:
 *  - kv: kulcs/érték sorok (add/remove/edit) — egyszerű string-specs
 *  - raw: JSON textarea — tetszőleges (nested/typed) specs, típus-megőrző
 *
 * A kanonikus érték a JSON-SZÖVEG (`value` prop): a page FormState string-only
 * dirty-compare idiómájához igazodik. Üres string = nincs specs (specsJson=null).
 *
 * Nézet-szabályok:
 *  - kv CSAK akkor elérhető, ha a value üres VAGY parse-olható objektum csupa
 *    primitív (string/number/boolean) értékkel — különben a kv-szerializáció
 *    típus-vesztő lenne (nested → string), ezért raw kényszerített.
 *  - a kv-ből mentett értékek STRING-ek (divergencia-napló: a tipikus specs
 *    "4.0 kW" jellegű; typed értékhez a raw nézet való).
 */

import { Button } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';

interface SpecsJsonEditorProps {
  /** Kanonikus JSON-szöveg ('' = nincs specs). */
  value: string;
  /** Minden szerkesztés a friss JSON-szöveggel hív vissza ('' = üres). */
  onChange: (next: string) => void;
}

type SpecRow = { key: string; value: string };

/** A value → kv-sorok, ha típus-vesztés nélkül ábrázolható; különben null. */
function parseToRows(value: string): SpecRow[] | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const rows: SpecRow[] = [];
  for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      rows.push({ key: k, value: String(v) });
    } else {
      return null; // nested/null érték → kv típus-vesztő lenne
    }
  }
  return rows;
}

/** kv-sorok → JSON-szöveg. Üres kulcsú sorok kimaradnak; 0 pár = ''. */
function rowsToJson(rows: SpecRow[]): string {
  const entries = rows.filter(r => r.key.trim().length > 0);
  if (entries.length === 0) return '';
  const obj: Record<string, string> = {};
  for (const r of entries) obj[r.key.trim()] = r.value;
  return JSON.stringify(obj, null, 2);
}

function isValidJsonObject(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return true;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed);
  } catch {
    return false;
  }
}

export function SpecsJsonEditor({ value, onChange }: SpecsJsonEditorProps) {
  const { t } = useTranslation();

  const parsedRows = useMemo(() => parseToRows(value), [value]);
  const kvAvailable = parsedRows !== null;

  // Lokál sor-state: az üres-kulcsú (még gépelés alatti) sorok megőrzéséhez —
  // a kanonikus value-ból az üres kulcs kiesne. A value-ból hydratálunk, de a
  // lokál szerkesztés alatt a rows az igazság, és minden változás kiírja a JSON-t.
  const [localRows, setLocalRows] = useState<SpecRow[] | null>(null);
  const rows = localRows ?? parsedRows ?? [];

  const [view, setView] = useState<'kv' | 'raw'>(kvAvailable ? 'kv' : 'raw');
  const effectiveView = kvAvailable ? view : 'raw';

  const rawInvalid = !isValidJsonObject(value);

  const applyRows = (next: SpecRow[]) => {
    setLocalRows(next);
    onChange(rowsToJson(next));
  };

  const updateRow = (index: number, patch: Partial<SpecRow>) => {
    applyRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  return (
    <div data-testid="specs-editor" className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="specs-view-kv"
          disabled={!kvAvailable}
          onClick={() => {
            setView('kv');
            setLocalRows(parsedRows);
          }}
          className={`rounded-md px-2 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
            effectiveView === 'kv'
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-muted'
          }`}
        >
          {t('equipment.types.editor.specs.kvView')}
        </button>
        <button
          type="button"
          data-testid="specs-view-raw"
          onClick={() => {
            setView('raw');
            setLocalRows(null);
          }}
          className={`rounded-md px-2 py-1 text-xs font-semibold transition-colors ${
            effectiveView === 'raw'
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-muted'
          }`}
        >
          {t('equipment.types.editor.specs.rawView')}
        </button>
      </div>

      {effectiveView === 'kv' ? (
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={row.key}
                onChange={e => updateRow(i, { key: e.target.value })}
                placeholder={t('equipment.types.editor.specs.keyPlaceholder')}
                className="w-2/5 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                data-testid={`specs-row-key-${i}`}
              />
              <input
                type="text"
                value={row.value}
                onChange={e => updateRow(i, { value: e.target.value })}
                placeholder={t('equipment.types.editor.specs.valuePlaceholder')}
                className="flex-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                data-testid={`specs-row-value-${i}`}
              />
              <button
                type="button"
                onClick={() => applyRows(rows.filter((_, j) => j !== i))}
                title={t('equipment.types.editor.specs.removeRow')}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950"
                data-testid={`specs-remove-row-${i}`}
              >
                ×
              </button>
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => applyRows([...rows, { key: '', value: '' }])}
            data-testid="specs-add-row"
          >
            + {t('equipment.types.editor.specs.addRow')}
          </Button>
        </div>
      ) : (
        <div className="space-y-1">
          <textarea
            value={value}
            onChange={e => onChange(e.target.value)}
            rows={6}
            spellCheck={false}
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 font-mono text-sm"
            data-testid="specs-raw-textarea"
          />
          {rawInvalid && (
            <p
              data-testid="specs-invalid-json"
              className="text-xs text-rose-600 dark:text-rose-400"
            >
              {t('equipment.types.editor.specs.invalidJson')}
            </p>
          )}
          {!kvAvailable && !rawInvalid && (
            <p className="text-xs text-muted-foreground">
              {t('equipment.types.editor.specs.nonPrimitiveHint')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
